import {
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  VStack,
  Text,
  HStack,
  Stack,
  InputGroup,
  InputLeftElement,
  Input,
  FormControl,
  Textarea,
  FormErrorMessage,
  Spacer,
  Button,
  Box,
  Checkbox,
  DrawerFooter,
  Spinner,
} from "@chakra-ui/react";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  CirclePlusIcon,
  EyeIcon,
  GroupMembershipIcon,
  HelpIcon,
  ClockArrowPlusIcon,
  InfoIcon,
} from "modules/icons";
import colors from "app/colors";
import { FieldValues, useForm } from "react-hook-form";
import {
  NEW_DISTRIBUTION_GROUP_NAME_REGEX,
  SERVER_ERROR,
} from "modules/utils/Constants";
import { getMessageFromError, getUserFullName } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { RuleGroupType } from "react-querybuilder";
import {
  InternalAdminCreateDistributionGroupMutation,
  InternalAdminCreateDistributionGroupMutationVariables,
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
  InternalAdminUserFragment,
} from "gql/graphql";
import { useMutation } from "@apollo/client";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";
import INTERNAL_ADMIN_CREATE_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-create-distribution-group";
import useInternalAdminDistributionGroups from "../hooks/use-internal-admin-distribution-groups";
import useInternalAdminUserList from "../hooks/use-internal-admin-user-list";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import useInternalAdminTags from "../hooks/use-internal-admin-tags";
import InternalAdminUserTagGroupSelector, {
  Option,
} from "../components/internal-admin-user-tag-group-selector";
import useInternalAdminDistributionGroupQuery from "../hooks/use-internal-admin-distribution-group-query";
import InternalAdminEmailUserSelector from "../components/internal-admin-email-user-selector";
import useQueryString from "modules/hooks/use-query-string";
import InternalAdminDistributionGroupDeleteQueryButton from "../buttons/internal-admin-distribution-group-delete-query-button";

interface IProps {
  distributionGroupId?: string;
}

const InternalAdminAddEditDistributionGroupDrawer = ({
  distributionGroupId,
}: IProps) => {
  const { openModal, closeDrawer } = useOverlay();
  const { qsOrganizationId } = useQueryString();
  const [tagIds, setTagIds] = useState<Array<string>>([]);
  const [addedUserIds, setAddedUserIds] = useState<Array<string>>([]);
  const [distributionGroupIds, setDistributionGroupIds] = useState<
    Array<string>
  >([]);

  const { distributionGroup, loading: loadingDistributionGroup } =
    useInternalAdminDistributionGroups(distributionGroupId);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [query, setQuery] = useState<RuleGroupType | undefined>();

  useEffect(() => {
    if (!distributionGroup) return;
    setQuery(
      distributionGroup.queryBuilder
        ? JSON.parse(distributionGroup.queryBuilder)
        : undefined,
    );
  }, [distributionGroup]);

  const { naturalLanguageQuery, mongoDbQuery, serializedQuery } =
    useInternalAdminDistributionGroupQuery({
      query:
        query ??
        (distributionGroup?.queryBuilder
          ? JSON.parse(distributionGroup.queryBuilder)
          : undefined),
    });

  const [internalAdminCreateDistributionGroupMutationAPI] = useMutation<
    InternalAdminCreateDistributionGroupMutation,
    InternalAdminCreateDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_CREATE_DISTRIBUTION_GROUP_MUTATION);

  const [internalAdminEditDistributionGroupMutationAPI] = useMutation<
    InternalAdminEditDistributionGroupMutation,
    InternalAdminEditDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const { users: allDistributionGroupsUsers } = useInternalAdminUserList({
    organizationId: distributionGroup?.organizationId,
    input: {
      limit: 1000,
      offset: 0,
      allDistributionGroups: true,
    },
  });

  const allDistributionGroupsUserNames = useMemo(
    () =>
      allDistributionGroupsUsers
        .filter((user) => user.isAllDistributionGroups === true)
        .map((user) => getUserFullName(user))
        .join(", "),
    [allDistributionGroupsUsers],
  );

  const onSelectedTagsChanged = useCallback((options: Option[]) => {
    setTagIds(options.map((obj) => obj.id));
  }, []);

  const onSelectedUsersDistributionGroupsChanged = useCallback(
    (options: Option[]) => {
      setAddedUserIds(
        options.filter((obj) => obj.type === "user").map((obj) => obj.id),
      );
      setDistributionGroupIds(
        options
          .filter((obj) => obj.type === "distribution-group")
          .map((obj) => obj.id),
      );
    },
    [],
  );

  const onSaveQuery = useCallback((query: RuleGroupType) => {
    setQuery(query);
  }, []);

  const onEditDynamicRulesClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_DISTRIBUTION_GROUP_QUERY,
      onSaveCallback: onSaveQuery,
      query,
    });
  }, [onSaveQuery, openModal, query]);

  const [emailAddresses, setEmailAddresses] = useState<
    Array<InternalAdminUserFragment>
  >([]);

  const onEmailUsersChanged = useCallback(
    (value: InternalAdminUserFragment[]) => {
      setEmailAddresses(value);
    },
    [],
  );

  const openHistoricalAccessHelpModal = useCallback(() => {
    openModal({
      type: MODAL_TYPE.HELP,
      helpModal: {
        icon: <ClockArrowPlusIcon boxSize={14} fill={colors.blackSecondary} />,
        title: "Grant Historical Access",
        message: (
          <Text>
            When new members are automatically added to this group, this option
            gives them immediate access to{" "}
            <Text fontWeight="700" as="span">
              ALL
            </Text>{" "}
            threads that include this group.
          </Text>
        ),
      },
    });
  }, [openModal]);

  const { users: addedEmailUsers } = useInternalAdminUsers(
    distributionGroup?.addedEmailUserIdList,
  );

  const defaultEmailUsers = useMemo(
    (): InternalAdminUserFragment[] =>
      addedEmailUsers.map((user) => ({
        ...user,
        __isNew__: false,
      })),
    [addedEmailUsers],
  );

  const addedEmailUserIds = useMemo(
    () => emailAddresses.map((emailAddress) => emailAddress.id),
    [emailAddresses],
  );

  const { tags: tagsData, loading: loadingTags } = useInternalAdminTags(
    distributionGroup?.tagIdList,
  );

  const defaultTags = useMemo(() => {
    if (!distributionGroup || !tagsData) return [];
    return tagsData
      .filter((tag) => tag.active)
      .map(
        (tag): Option => ({
          id: tag.id,
          active: tag.active,
          data: tag,
          type: "tag",
        }),
      );
  }, [distributionGroup, tagsData]);

  const { users: addedUsers, loading: loadingAddedUsers } =
    useInternalAdminUsers(distributionGroup?.addedUserIdList);

  const {
    distributionGroups: addedDistributionGroups,
    loading: loadingAddedDistributionGroups,
  } = useInternalAdminDistributionGroups(
    distributionGroup?.distributionGroupIdList,
  );

  const defaultAddedUsersAndDistributionGroups = useMemo(() => {
    if (!distributionGroup) return [];
    const options: Option[] = [];
    addedUsers.forEach((user) => {
      options.push({
        id: user.id,
        active: user.active,
        data: user,
        type: "user",
      });
    });
    addedDistributionGroups.forEach((distributionGroup) => {
      options.push({
        id: distributionGroup.id,
        active: distributionGroup.active,
        data: distributionGroup,
        type: "distribution-group",
      });
    });
    return options;
  }, [addedDistributionGroups, addedUsers, distributionGroup]);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  const isAllActiveUsers: boolean = watch("allActiveUsers", false);

  const onPreviewUsersClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_DISTRIBUTION_GROUP_PREVIEW_USERS,
      adminPreviewDistributionGroupMembershipInput: {
        distributionGroupIdList: distributionGroupIds,
        isAllUsers: isAllActiveUsers,
        addedUserIdList: addedUserIds,
        addedEmailUserIdList: addedEmailUserIds,
        query: distributionGroup?.queryBuilder,
      },
      query,
    });
  }, [
    addedEmailUserIds,
    addedUserIds,
    distributionGroup?.queryBuilder,
    distributionGroupIds,
    isAllActiveUsers,
    openModal,
    query,
  ]);

  const onSubmit = useCallback(
    (_fieldValues: FieldValues) => {
      if (!qsOrganizationId) return;
      setIsSubmitting(true);
      if (distributionGroup) {
        internalAdminEditDistributionGroupMutationAPI({
          variables: {
            organizationId: qsOrganizationId,
            id: distributionGroup.id,
            input: {
              name: _fieldValues.name,
              description: _fieldValues.description,
              tagIdList: tagIds,
              query: mongoDbQuery,
              queryBuilder: serializedQuery,
              isAllUsers: _fieldValues.allActiveUsers,
              isHistoricalAccess: _fieldValues.historicalAccess,
              distributionGroupIdList: distributionGroupIds,
              addedUserIdList: addedUserIds,
              addedEmailUserIdList: addedEmailUserIds,
            },
          },
          fetchPolicy: "network-only",
          onCompleted: () => {
            setIsSubmitting(false);
            closeDrawer();
          },
          onError: (error) => {
            setIsSubmitting(false);
            setError(SERVER_ERROR, {
              message: getMessageFromError(error.message),
            });
          },
        });
        return;
      }
      internalAdminCreateDistributionGroupMutationAPI({
        variables: {
          organizationId: qsOrganizationId,
          input: {
            name: _fieldValues.name,
            description: _fieldValues.description,
            tagIdList: tagIds,
            notificationUserIdList: addedUserIds,
            query: mongoDbQuery,
            queryBuilder: serializedQuery,
            isAllUsers: _fieldValues.allActiveUsers,
            isHistoricalAccess: _fieldValues.historicalAccess,
            distributionGroupIdList: distributionGroupIds,
            addedUserIdList: addedUserIds,
            addedEmailUserIdList: addedEmailUserIds,
          },
        },
        onCompleted: () => {
          setIsSubmitting(false);
          closeDrawer();
        },
        onError: (error) => {
          setIsSubmitting(false);
          setError(SERVER_ERROR, {
            message: getMessageFromError(error.message),
          });
        },
      });
    },
    [
      addedEmailUserIds,
      addedUserIds,
      closeDrawer,
      distributionGroup,
      distributionGroupIds,
      internalAdminCreateDistributionGroupMutationAPI,
      internalAdminEditDistributionGroupMutationAPI,
      mongoDbQuery,
      qsOrganizationId,
      serializedQuery,
      setError,
      tagIds,
    ],
  );

  if (
    loadingDistributionGroup ||
    loadingAddedDistributionGroups ||
    loadingAddedUsers ||
    loadingTags
  ) {
    return null;
  }

  return (
    <form
      id="internal-admin-create-distribution-group-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <DrawerContent>
        <DrawerCloseButton top={5} left={5} color={colors.gray30} />
        <DrawerHeader>
          <Center fontSize={26} fontFamily="Outfit" fontWeight={700}>
            {distributionGroup ? `Edit Group` : `Create Group`}
          </Center>
          <Center height={10}>
            <Divider />
          </Center>
        </DrawerHeader>
        <DrawerBody>
          <Stack w="full" spacing={8}>
            <VStack w="full" alignItems="flex-start">
              <HStack w="full">
                <InfoIcon />
                <Text fontSize={18} fontWeight={600}>
                  Info
                </Text>
              </HStack>
              <Divider borderColor={colors.black} />
              <VStack w="full" alignItems="flex-start" spacing={4}>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <FormControl isInvalid={!!errors?.name}>
                    <Text fontSize={16} fontWeight={500}>
                      Name
                    </Text>
                    <InputGroup>
                      <InputLeftElement color={colors.gray30} mx={0}>
                        @
                      </InputLeftElement>
                      <Input
                        id="name"
                        pl={8}
                        autoComplete="off"
                        autoFocus
                        defaultValue={
                          distributionGroup ? distributionGroup.name : undefined
                        }
                        _placeholder={{
                          color: colors.gray30,
                        }}
                        {...register("name", {
                          required: {
                            message: "The name is required",
                            value: true,
                          },
                          maxLength: {
                            value: 30,
                            message: "The name must be 30 characters or less",
                          },
                          pattern: {
                            value: NEW_DISTRIBUTION_GROUP_NAME_REGEX,
                            message:
                              "Only lowercase letters (a-z), numbers (0-9), hyphens (-), periods (.), and underscores (_) are allowed.",
                          },
                        })}
                      />
                    </InputGroup>
                    <Text
                      color={colors.grayMain}
                      fontSize={12}
                      fontWeight={400}
                    >
                      Special characters allowed: dashes, underscores and
                      periods
                    </Text>
                    <FormErrorMessage>
                      {getMessageFromError(errors?.name?.message)}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <HStack alignItems="flex-end">
                    <Text fontSize={16} fontWeight={500}>
                      Tag(s)
                    </Text>
                    <Text
                      color={colors.grayMain}
                      fontSize={12}
                      fontWeight={500}
                    >
                      Optional
                    </Text>
                  </HStack>
                  <Text color={colors.grayMain} fontSize={12} fontWeight={400}>
                    Automatically add tags to threads sent to this group.
                  </Text>
                  <InternalAdminUserTagGroupSelector
                    types={["tag"]}
                    onSelectedOptionsChanged={onSelectedTagsChanged}
                    defaultValues={defaultTags}
                    isMultiSelect
                    menuPlacement="bottom"
                    allowInactive={false}
                  />
                </VStack>
                <FormControl isInvalid={!!errors?.description}>
                  <HStack alignItems="flex-end">
                    <Text fontSize={16} fontWeight={500}>
                      Description
                    </Text>
                    <Text
                      color={colors.grayMain}
                      fontSize={12}
                      fontWeight={500}
                    >
                      Optional
                    </Text>
                  </HStack>
                  <Textarea
                    id="description"
                    defaultValue={
                      distributionGroup && distributionGroup.description != null
                        ? distributionGroup.description
                        : undefined
                    }
                    placeholder="Add a short description of how this group should be used."
                    _placeholder={{
                      fontStyle: "italic",
                      color: colors.placeholderText,
                    }}
                    {...register("description", {
                      maxLength: {
                        value: 1024,
                        message:
                          "The description must be 1024 characters or less",
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.name?.message)}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </VStack>
            <VStack w="full" alignItems="flex-start">
              <HStack w="full">
                <GroupMembershipIcon boxSize={6} />
                <Text fontSize={18} fontWeight={600}>
                  Membership Criteria
                </Text>
                <Spacer />
                <Button
                  variant="outline"
                  alignItems="center"
                  fontSize={12}
                  fontWeight={600}
                  color={colors.blue}
                  borderColor={colors.blue}
                  size="xs"
                  leftIcon={<EyeIcon boxSize={4} fill={colors.blue} />}
                  onClick={onPreviewUsersClicked}
                >
                  View Member List
                </Button>
              </HStack>
              <Divider borderColor={colors.black} />
              <HStack spacing={0}>
                <Checkbox
                  id="historicalAccess"
                  {...register("historicalAccess")}
                  borderColor={colors.gray40}
                  defaultChecked={
                    distributionGroup
                      ? distributionGroup.isHistoricalAccess
                      : undefined
                  }
                >
                  <Text fontSize={14} fontWeight={700} color={colors.gray50} />
                  Grant historical access when users are added
                </Checkbox>
                <Button
                  onClick={openHistoricalAccessHelpModal}
                  variant="hover"
                  size="xs"
                >
                  <HelpIcon fill={colors.gray40} boxSize={4} />
                </Button>
              </HStack>
              <VStack w="full" alignItems="flex-start" spacing={0}>
                <Text
                  fontSize={16}
                  fontWeight={700}
                  color={colors.blackSecondary}
                >
                  Users in Organization
                </Text>
                <Text fontSize={12} fontWeight={400} color={colors.grayMain}>
                  Define which users in the organization are members of this
                  group
                </Text>
              </VStack>
              {!isAllActiveUsers && (
                <VStack
                  w="full"
                  alignItems="flex-start"
                  bg={colors.whiteTertiary}
                  borderRadius={4}
                  p={2}
                  spacing={4}
                >
                  <VStack spacing={0} w="full" alignItems="flex-start">
                    <Text
                      fontSize={14}
                      fontWeight={500}
                      color={colors.blackSecondary}
                    >
                      Individual Users & Groups
                    </Text>
                    <Text
                      fontSize={12}
                      fontWeight={400}
                      color={colors.grayMain}
                    >
                      Specify which users and other groups are members
                    </Text>
                    <Box w="full" bg={colors.white} borderRadius={8}>
                      <InternalAdminUserTagGroupSelector
                        types={["user", "distribution-group"]}
                        onSelectedOptionsChanged={
                          onSelectedUsersDistributionGroupsChanged
                        }
                        defaultValues={defaultAddedUsersAndDistributionGroups}
                        isMultiSelect
                        menuPlacement="bottom"
                        allowInactive={false}
                        excludedIds={
                          distributionGroupId ? [distributionGroupId] : []
                        }
                      />
                    </Box>
                  </VStack>
                  <VStack w="full" alignItems="flex-start" spacing={0}>
                    <Text
                      fontSize={14}
                      fontWeight={500}
                      color={colors.blackSecondary}
                    >
                      Dynamic Rules
                    </Text>
                    <Text
                      fontSize={12}
                      fontWeight={400}
                      color={colors.grayMain}
                    >
                      Automatically include users who match rules below
                    </Text>
                    {naturalLanguageQuery && (
                      <Text fontSize={14}>{naturalLanguageQuery}</Text>
                    )}
                    <HStack spacing={4}>
                      <Button
                        variant="outline"
                        alignItems="center"
                        fontSize={12}
                        fontWeight={600}
                        color={colors.blue}
                        borderColor={colors.blue}
                        size="xs"
                        leftIcon={
                          <CirclePlusIcon boxSize={4} fill={colors.blue} />
                        }
                        onClick={onEditDynamicRulesClicked}
                      >
                        Edit Membership Rules
                      </Button>
                      <InternalAdminDistributionGroupDeleteQueryButton
                        distributionGroup={distributionGroup}
                        onDelete={() => setQuery(undefined)}
                      />
                    </HStack>
                  </VStack>
                  {allDistributionGroupsUserNames.length > 0 && (
                    <VStack spacing={0} w="full" alignItems="flex-start">
                      <Text
                        fontWeight={500}
                        fontSize={14}
                        color={colors.blackSecondary}
                      >
                        Included in All Groups
                      </Text>
                      <Text
                        fontWeight={400}
                        fontSize={14}
                        color={colors.gray50}
                      >
                        {allDistributionGroupsUserNames}
                      </Text>
                    </VStack>
                  )}
                </VStack>
              )}
              <Checkbox
                id="allActiveUsers"
                {...register("allActiveUsers")}
                borderColor={colors.gray40}
                defaultChecked={
                  distributionGroup ? distributionGroup.isAllUsers : undefined
                }
              >
                <Text fontSize={14} fontWeight={700} color={colors.gray50} />
                All Active Users
              </Checkbox>
              <Divider />
              <VStack w="full" alignItems="flex-start" spacing={0}>
                <Text
                  fontWeight={700}
                  fontSize={16}
                  color={colors.blackSecondary}
                >
                  External Contacts
                </Text>
                <Text fontWeight={400} fontSize={12} color={colors.grayMain}>
                  Add contacts outside organization to receive messages via
                  email that use this group.
                </Text>
                <InternalAdminEmailUserSelector
                  allowCreation
                  onChange={onEmailUsersChanged}
                  defaultValue={defaultEmailUsers}
                  isMultiSelect
                />
              </VStack>
            </VStack>
          </Stack>
        </DrawerBody>
        <DrawerFooter h={100} boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)">
          <Center w="full">
            <VStack>
              {errors.SERVER_ERROR && (
                <Text color={colors.red}>
                  {getMessageFromError(errors?.SERVER_ERROR?.message)}
                </Text>
              )}
              <HStack spacing={12}>
                <Button
                  variant="cancel"
                  h="40px"
                  w="140px"
                  onClick={(e) => {
                    clearErrors([SERVER_ERROR]);
                    e.stopPropagation();
                    closeDrawer();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  h="40px"
                  w="140px"
                  type="submit"
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </VStack>
          </Center>
        </DrawerFooter>
      </DrawerContent>
    </form>
  );
};

export default memo(InternalAdminAddEditDistributionGroupDrawer);
